import { collection, doc, onSnapshot, query, where, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/client";
import {
  applyTripWorkspaceSection,
  exportTripWorkspaceSection,
  hasLocalTripWorkspaceSection,
  listTrips,
  tripWorkspaceSectionIds,
  upsertTripSummary,
  reconcileOwnedTripSummariesFromServer,
  type TripSummary,
  type TripWorkspaceSectionId
} from "./tripStore";
import { normalizeTripRole, type EffectiveTripRole } from "./tripPermissions";

type CloudSectionDocument = { data?: unknown; revision?: number };
type CloudWorkspaceSnapshot = {
  sections?: Array<{ section?: string; data?: unknown; revision?: number; updatedAt?: string }>;
};

export type TripSyncState = "idle" | "syncing" | "synced" | "offline" | "error";

type PersistedWorkspaceBase = { data: unknown; revision: number };

function workspaceBaseKey(uid: string, tripId: string, section: TripWorkspaceSectionId): string {
  return `tripai.workspaceSyncBase.v1.${uid}.${tripId}.${section}`;
}

function readWorkspaceBase(uid: string, tripId: string, section: TripWorkspaceSectionId): PersistedWorkspaceBase | null {
  try {
    const raw = localStorage.getItem(workspaceBaseKey(uid, tripId, section));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data?: unknown; revision?: unknown };
    return { data: parsed.data ?? null, revision: Number.isFinite(Number(parsed.revision)) ? Number(parsed.revision) : 0 };
  } catch {
    return null;
  }
}

function writeWorkspaceBase(uid: string, tripId: string, section: TripWorkspaceSectionId, data: unknown, revision: number): void {
  try {
    localStorage.setItem(workspaceBaseKey(uid, tripId, section), JSON.stringify({ data: data ?? null, revision }));
  } catch {
    // Sync metadata is an optimization; never block the user's local workspace.
  }
}

function stableSerialize(value: unknown): string {
  return JSON.stringify(value ?? null);
}

function parseTimestamp(value: unknown): number {
  if (!value || typeof value !== "object") return 0;
  const candidate = value as { updatedAt?: unknown; createdAt?: unknown };
  const raw = String(candidate.updatedAt || candidate.createdAt || "");
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasStableId(value: unknown): value is { id: string; updatedAt?: unknown; createdAt?: unknown } {
  return Boolean(value && typeof value === "object" && typeof (value as { id?: unknown }).id === "string" && (value as { id: string }).id);
}

function mergeConcurrentArrays(base: unknown[], local: unknown[], remote: unknown[]): unknown[] {
  const all = [...base, ...local, ...remote];
  if (all.some((item) => !hasStableId(item))) {
    if (stableSerialize(local) === stableSerialize(base)) return remote;
    if (stableSerialize(remote) === stableSerialize(base)) return local;
    return local;
  }

  const baseById = new Map(base.map((item) => [(item as { id: string }).id, item]));
  const localById = new Map(local.map((item) => [(item as { id: string }).id, item]));
  const remoteById = new Map(remote.map((item) => [(item as { id: string }).id, item]));
  const ids = new Set([...baseById.keys(), ...localById.keys(), ...remoteById.keys()]);
  const merged: unknown[] = [];

  for (const id of ids) {
    const baseItem = baseById.get(id);
    const localItem = localById.get(id);
    const remoteItem = remoteById.get(id);
    const baseSig = stableSerialize(baseItem);
    const localSig = stableSerialize(localItem);
    const remoteSig = stableSerialize(remoteItem);

    let winner: unknown;
    if (localSig === baseSig) winner = remoteItem;
    else if (remoteSig === baseSig) winner = localItem;
    else if (localSig === remoteSig) winner = localItem;
    else if (localItem === undefined && remoteItem !== undefined) {
      winner = parseTimestamp(remoteItem) > parseTimestamp(baseItem) ? remoteItem : undefined;
    } else if (remoteItem === undefined && localItem !== undefined) {
      winner = parseTimestamp(localItem) > parseTimestamp(baseItem) ? localItem : undefined;
    } else {
      winner = parseTimestamp(remoteItem) > parseTimestamp(localItem) ? remoteItem : localItem;
    }

    if (winner !== undefined) merged.push(winner);
  }

  return merged;
}

function mergeConcurrentData(base: unknown, local: unknown, remote: unknown): unknown {
  const baseSig = stableSerialize(base);
  const localSig = stableSerialize(local);
  const remoteSig = stableSerialize(remote);

  if (localSig === baseSig) return remote;
  if (remoteSig === baseSig) return local;
  if (localSig === remoteSig) return local;

  if (Array.isArray(local) && Array.isArray(remote)) {
    return mergeConcurrentArrays(Array.isArray(base) ? base : [], local, remote);
  }

  if (
    local && remote &&
    typeof local === "object" && typeof remote === "object" &&
    !Array.isArray(local) && !Array.isArray(remote)
  ) {
    const baseObject = base && typeof base === "object" && !Array.isArray(base) ? base as Record<string, unknown> : {};
    const result: Record<string, unknown> = {};
    const keys = new Set([
      ...Object.keys(baseObject),
      ...Object.keys(local as Record<string, unknown>),
      ...Object.keys(remote as Record<string, unknown>)
    ]);
    for (const key of keys) {
      result[key] = mergeConcurrentData(
        baseObject[key],
        (local as Record<string, unknown>)[key],
        (remote as Record<string, unknown>)[key]
      );
    }
    return result;
  }

  return local;
}

function pendingTripSummaryKey(uid: string): string {
  return `tripai.tripSummarySync.pending.v1.${uid}`;
}

function readPendingTripSummaries(uid: string): Record<string, TripSummary> {
  try {
    const raw = localStorage.getItem(pendingTripSummaryKey(uid));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, TripSummary>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePendingTripSummaries(uid: string, pending: Record<string, TripSummary>): void {
  try {
    if (Object.keys(pending).length === 0) localStorage.removeItem(pendingTripSummaryKey(uid));
    else localStorage.setItem(pendingTripSummaryKey(uid), JSON.stringify(pending));
  } catch {
    // Queue metadata must never block local trip editing.
  }
}

export function enqueueTripSummaryCloudSync(uid: string, trip: TripSummary): void {
  const pending = readPendingTripSummaries(uid);
  pending[trip.id] = trip;
  writePendingTripSummaries(uid, pending);
}

const summaryFlushes = new Map<string, Promise<number>>();

export function flushPendingTripSummaryCloudSync(uid: string): Promise<number> {
  const current = summaryFlushes.get(uid);
  if (current) return current;
  const task = flushTripSummaries(uid);
  summaryFlushes.set(uid, task);
  void task.finally(() => { if (summaryFlushes.get(uid) === task) summaryFlushes.delete(uid); }).catch(() => undefined);
  return task;
}

async function flushTripSummaries(uid: string): Promise<number> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return 0;
  const pending = readPendingTripSummaries(uid);
  const entries = Object.values(pending);
  if (entries.length === 0) return 0;
  const saveSummary = httpsCallable<{ trip: TripSummary }, { saved: boolean }>(functions, "saveTripSummary");
  let completed = 0;
  for (const trip of entries) {
    try {
      await saveSummary({ trip });
      const latest = readPendingTripSummaries(uid);
      if (stableSerialize(latest[trip.id]) === stableSerialize(trip)) {
        delete latest[trip.id];
        writePendingTripSummaries(uid, latest);
      }
      completed += 1;
    } catch {
      // Keep the newest local summary queued for the next online/focus retry.
    }
  }
  return completed;
}

export function startTripCloudSync(input: {
  uid: string;
  trip: TripSummary;
  onRole: (role: EffectiveTripRole) => void;
  onRemoteUpdate: () => void;
  onTripSummary: (trip: TripSummary) => void;
  onSyncState?: (state: TripSyncState) => void;
}): () => void {
  const { uid, trip } = input;
  let stopped = false;
  let currentRole: EffectiveTripRole = trip.ownerUid === uid ? "owner" : normalizeTripRole(trip.memberRole);
  let applyingRemote = false;
  let workspaceReady = false;
  let summaryReady = false;
  let syncRunning = false;
  let lastSyncState: TripSyncState = "idle";
  let latestTrip = trip;
  let summarySignature = "";

  const reportSyncState = (state: TripSyncState) => {
    if (lastSyncState === state) return;
    lastSyncState = state;
    input.onSyncState?.(state);
  };
  const lastLocal = new Map<string, string>();
  const lastRemote = new Map<string, string>();
  const remoteBase = new Map<string, unknown>();
  const remoteRevision = new Map<string, number>();
  const unsubscribers: Unsubscribe[] = [];

  for (const section of tripWorkspaceSectionIds) {
    const persisted = readWorkspaceBase(uid, trip.id, section);
    if (!persisted) continue;
    remoteBase.set(section, persisted.data);
    remoteRevision.set(section, persisted.revision);
  }

  input.onRole(currentRole);
  reportSyncState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "syncing");

  const saveSection = httpsCallable<{
    tripId: string;
    section: string;
    data: unknown;
    baseData: unknown;
    baseRevision: number;
  }, { saved: boolean; revision: number }>(functions, "saveTripWorkspaceSection");
  const saveSummary = httpsCallable<{ trip: TripSummary }, { saved: boolean }>(functions, "saveTripSummary");
  const getWorkspaceSnapshot = httpsCallable<{ tripId: string }, CloudWorkspaceSnapshot>(functions, "getTripWorkspaceSnapshot");

  unsubscribers.push(onSnapshot(doc(db, "trips", trip.id), (snapshot) => {
    if(snapshot.metadata.fromCache && listTrips(uid).some(item=>item.id===trip.id))return;
    if (!snapshot.exists() || stopped) return;
    const value = snapshot.data() as Record<string, any>;
    const members = (value.members || {}) as Record<string, { role?: string }>;
    const cloudOwnerUid = String(value.ownerUid || "").trim();
    const isOwner = cloudOwnerUid === uid || (!cloudOwnerUid && latestTrip.ownerUid === uid);
    currentRole = normalizeTripRole(members[uid]?.role, isOwner);
    input.onRole(currentRole);
    latestTrip = {
      ...latestTrip,
      id: String(value.id || latestTrip.id),
      ownerUid: cloudOwnerUid || latestTrip.ownerUid,
      name: String(value.name || latestTrip.name),
      destination: String(value.destination || latestTrip.destination || ""),
      startDate: String(value.startDate || latestTrip.startDate || ""),
      endDate: String(value.endDate || latestTrip.endDate || ""),
      status: (value.status || latestTrip.status) as TripSummary["status"],
      profilePhotoGooglePlaceId: String(value.profilePhotoGooglePlaceId || "") || undefined,
      profilePhotoIndex: Number.isFinite(Number(value.profilePhotoIndex)) ? Number(value.profilePhotoIndex) : undefined,
      profilePhotoDataUrl: String(value.profilePhotoDataUrl || "") || undefined,
      coverPhotoGooglePlaceId: String(value.coverPhotoGooglePlaceId || "") || undefined,
      coverPhotoIndex: Number.isFinite(Number(value.coverPhotoIndex)) ? Number(value.coverPhotoIndex) : undefined,
      coverPhotoDataUrl: String(value.coverPhotoDataUrl || "") || undefined,
      aiPhotosSelectedAt: String(value.aiPhotosSelectedAt || "") || undefined,
      archivedAt: String(value.archivedAt || "") || undefined,
      deletedAt: value.deletedAt === null ? null : (String(value.deletedAt || "") || undefined),
      memberRole: currentRole,
      createdAt: String(value.createdAt || latestTrip.createdAt),
      updatedAt: String(value.updatedAt || latestTrip.updatedAt)
    };
    summarySignature = JSON.stringify(latestTrip);
    summaryReady = true;
    const pendingSummary=readPendingTripSummaries(uid)[trip.id];
    if(pendingSummary)latestTrip={...latestTrip,...pendingSummary,memberRole:currentRole};
    latestTrip=upsertTripSummary(uid, latestTrip);
    input.onTripSummary(latestTrip);
  }, () => undefined));

  function applyRemoteSection(section: TripWorkspaceSectionId, payload: unknown, revision: number): boolean {
    const serialized = stableSerialize(payload);
    if (lastRemote.get(section) === serialized && remoteRevision.get(section) === revision && workspaceReady) return false;

    const hadLocalSection = hasLocalTripWorkspaceSection(uid, trip.id, section);
    const localData = exportTripWorkspaceSection(uid, trip.id, section);
    const previousRemote = remoteBase.has(section) ? remoteBase.get(section) : null;
    // On a new device many loaders return harmless default values even when no
    // local cache exists. Those defaults are NOT edits and must never win over
    // a populated cloud workspace. A device without a physical local section
    // therefore hydrates verbatim from the cloud; three-way merge is reserved
    // for devices that actually have local data.
    const merged = hadLocalSection
      ? mergeConcurrentData(previousRemote, localData, payload)
      : payload;
    const mergedSerialized = stableSerialize(merged);

    lastRemote.set(section, serialized);
    remoteBase.set(section, payload);
    remoteRevision.set(section, revision);
    writeWorkspaceBase(uid, trip.id, section, payload, revision);

    // If a true three-way merge produced something different from the cloud snapshot,
    // leave lastLocal at the cloud signature so the next sync tick writes the merged value.
    lastLocal.set(section, serialized);

    if (stableSerialize(localData) === mergedSerialized) return false;
    applyTripWorkspaceSection(uid, trip.id, section, merged);
    return true;
  }

  let hydrateRunning = false;
  async function hydrateWorkspace() {
    if (stopped || hydrateRunning) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) { reportSyncState("offline"); return; }
    hydrateRunning = true;
    reportSyncState("syncing");
    let changed = false;
    applyingRemote = true;
    try {
      const result = await getWorkspaceSnapshot({ tripId: trip.id });
      const sections = Array.isArray(result.data?.sections) ? result.data.sections : [];
      for (const item of sections) {
        const section = String(item.section || "") as TripWorkspaceSectionId;
        if (!tripWorkspaceSectionIds.includes(section)) continue;
        const revision = Number.isFinite(Number(item.revision)) ? Number(item.revision) : 0;
        if (applyRemoteSection(section, item.data, revision)) changed = true;
      }
      workspaceReady = true;
      reportSyncState("synced");
    } catch {
      reportSyncState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error");
      // Keep the local cache untouched. A later focus/timer/onSnapshot event retries.
    } finally {
      applyingRemote = false;
      hydrateRunning = false;
    }
    if (changed) input.onRemoteUpdate();
  }

  unsubscribers.push(onSnapshot(collection(db, "trips", trip.id, "workspace"), (snapshot) => {
    if (stopped) return;
    let changed = false;
    applyingRemote = true;
    try {
      for (const entry of snapshot.docs) {
        const section = entry.id as TripWorkspaceSectionId;
        if (!tripWorkspaceSectionIds.includes(section)) continue;
        const cloudDocument = entry.data() as CloudSectionDocument;
        const revision = Number.isFinite(Number(cloudDocument.revision)) ? Number(cloudDocument.revision) : 0;
        if (applyRemoteSection(section, cloudDocument.data, revision)) changed = true;
      }
      workspaceReady = true;
      reportSyncState("synced");
    } finally {
      applyingRemote = false;
    }
    if (changed) input.onRemoteUpdate();
  }, () => {
    reportSyncState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error");
    // Some deployments restrict direct client reads of workspace subcollections.
    // The authenticated callable uses the same role checks and guarantees hydration.
    void hydrateWorkspace();
  }));

  async function ensureOwnerCloudTrip() {
    if (trip.ownerUid !== uid) return;
    const currentLocalTrip = listTrips(uid).find((item) => item.id === trip.id) || trip;
    try {
      if (typeof navigator !== "undefined" && !navigator.onLine) { reportSyncState("offline"); return; }
      reportSyncState("syncing");
      await saveSummary({ trip: currentLocalTrip });
      latestTrip = currentLocalTrip;
      if (!summaryReady) summarySignature = JSON.stringify(currentLocalTrip);
      reportSyncState("synced");
    } catch {
      reportSyncState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error");
      // Retry on the periodic synchronization tick.
    }
  }

  async function syncTick() {
    if (stopped || applyingRemote || !workspaceReady || currentRole === "viewer" || syncRunning) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) { reportSyncState("offline"); return; }
    syncRunning = true;
    reportSyncState("syncing");
    let failed = false;

    try {
    if (currentRole === "owner" && summaryReady) {
      const currentLocalTrip = listTrips(uid).find((item) => item.id === trip.id) || latestTrip;
      latestTrip = currentLocalTrip;
      const localSignature = JSON.stringify(currentLocalTrip);
      if (localSignature !== summarySignature) {
        try {
          await saveSummary({ trip: currentLocalTrip });
          summarySignature = localSignature;
        } catch {
          failed = true;
        }
      }
    }

    if (latestTrip.archivedAt) {
      reportSyncState(failed ? "error" : "synced");
      return;
    }

    for (const section of tripWorkspaceSectionIds) {
      if (section === "participants" && currentRole !== "owner") continue;
      // A fresh device must never publish default empty values before another
      // device has migrated its real local workspace to the cloud. Upload only
      // sections that physically exist in this device cache or already exist
      // in the remote workspace snapshot.
      if (!remoteBase.has(section) && !hasLocalTripWorkspaceSection(uid, trip.id, section)) continue;
      const data = exportTripWorkspaceSection(uid, trip.id, section);
      const serialized = JSON.stringify(data ?? null);
      if (lastLocal.get(section) === serialized) continue;
      lastLocal.set(section, serialized);
      const baseData = remoteBase.has(section) ? remoteBase.get(section) : null;
      const baseRevision = remoteRevision.get(section) || 0;
      try {
        const result = await saveSection({ tripId: trip.id, section, data, baseData, baseRevision });
        if (Number.isFinite(Number(result.data?.revision))) {
          const revision = Number(result.data.revision);
          remoteRevision.set(section, revision);
          remoteBase.set(section, data);
          lastRemote.set(section, serialized);
          writeWorkspaceBase(uid, trip.id, section, data, revision);
        }
      } catch {
        failed = true;
        // Allow retry on next tick instead of permanently considering it synced.
        lastLocal.delete(section);
      }
    }
    reportSyncState(failed ? "error" : "synced");
    } finally {
      syncRunning = false;
    }
  }

  const hydrateOnVisible = () => {
    if (document.visibilityState === "visible") { void hydrateWorkspace(); void syncTick(); }
  };
  const hydrateOnFocus = () => { void hydrateWorkspace(); void syncTick(); };
  const syncOnOnline = () => { reportSyncState("syncing"); void hydrateWorkspace(); void ensureOwnerCloudTrip(); void syncTick(); };
  const syncOnOffline = () => reportSyncState("offline");
  const manualSync = () => { void hydrateWorkspace(); void ensureOwnerCloudTrip(); void syncTick(); };
  const syncOnWorkspaceChange = (event: Event) => {
    const detail = (event as CustomEvent<{ uid?: string; tripId?: string }>).detail;
    if (detail?.uid && detail.uid !== uid) return;
    if (detail?.tripId && detail.tripId !== trip.id) return;
    reportSyncState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "syncing");
    void syncTick();
  };

  void ensureOwnerCloudTrip();
  void hydrateWorkspace();
  const timer = window.setInterval(() => void syncTick(), 1400);
  const hydrationTimer = window.setInterval(() => void hydrateWorkspace(), 12000);
  document.addEventListener("visibilitychange", hydrateOnVisible);
  window.addEventListener("focus", hydrateOnFocus);
  window.addEventListener("online", syncOnOnline);
  window.addEventListener("offline", syncOnOffline);
  window.addEventListener("tripai:sync-now", manualSync);
  window.addEventListener("tripai:workspace-updated", syncOnWorkspaceChange as EventListener);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.clearInterval(hydrationTimer);
    document.removeEventListener("visibilitychange", hydrateOnVisible);
    window.removeEventListener("focus", hydrateOnFocus);
    window.removeEventListener("online", syncOnOnline);
    window.removeEventListener("offline", syncOnOffline);
    window.removeEventListener("tripai:sync-now", manualSync);
    window.removeEventListener("tripai:workspace-updated", syncOnWorkspaceChange as EventListener);
    for (const unsubscribe of unsubscribers) unsubscribe();
  };
}



function tripSummaryFromCloud(uid: string, id: string, value: Record<string, any>): TripSummary {
  const ownerUid = String(value.ownerUid || "");
  const members = (value.members || {}) as Record<string, { role?: string; participantRole?: string }>;
  const memberRole = normalizeTripRole(members[uid]?.participantRole || members[uid]?.role, ownerUid === uid);
  return {
    id,
    ownerUid,
    name: String(value.name || ""),
    destination: String(value.destination || ""),
    startDate: String(value.startDate || ""),
    endDate: String(value.endDate || ""),
    status: (value.status || "planning") as TripSummary["status"],
    profilePhotoGooglePlaceId: String(value.profilePhotoGooglePlaceId || "") || undefined,
    profilePhotoIndex: Number.isFinite(Number(value.profilePhotoIndex)) ? Number(value.profilePhotoIndex) : undefined,
    profilePhotoDataUrl: String(value.profilePhotoDataUrl || "") || undefined,
    coverPhotoGooglePlaceId: String(value.coverPhotoGooglePlaceId || "") || undefined,
    coverPhotoIndex: Number.isFinite(Number(value.coverPhotoIndex)) ? Number(value.coverPhotoIndex) : undefined,
    coverPhotoDataUrl: String(value.coverPhotoDataUrl || "") || undefined,
    aiPhotosSelectedAt: String(value.aiPhotosSelectedAt || "") || undefined,
    archivedAt: String(value.archivedAt || "") || undefined,
    deletedAt: value.deletedAt === null ? null : (String(value.deletedAt || "") || undefined),
    memberRole,
    createdAt: String(value.createdAt || new Date().toISOString()),
    updatedAt: String(value.updatedAt || value.createdAt || new Date().toISOString())
  };
}

async function reconcileWorkspaceOnce(uid: string, trip: TripSummary): Promise<boolean> {
  const getSnapshot = httpsCallable<{ tripId: string }, CloudWorkspaceSnapshot>(functions, "getTripWorkspaceSnapshot");
  const saveSection = httpsCallable<{
    tripId: string; section: string; data: unknown; baseData: unknown; baseRevision: number;
  }, { saved: boolean; revision: number }>(functions, "saveTripWorkspaceSection");

  const result = await getSnapshot({ tripId: trip.id });
  const remoteBySection = new Map<TripWorkspaceSectionId, { data: unknown; revision: number }>();
  for (const item of Array.isArray(result.data?.sections) ? result.data.sections : []) {
    const section = String(item.section || "") as TripWorkspaceSectionId;
    if (!tripWorkspaceSectionIds.includes(section)) continue;
    remoteBySection.set(section, {
      data: item.data ?? null,
      revision: Number.isFinite(Number(item.revision)) ? Number(item.revision) : 0
    });
  }

  let localChanged = false;
  const role = trip.ownerUid === uid ? "owner" : normalizeTripRole(trip.memberRole);
  for (const section of tripWorkspaceSectionIds) {
    const remote = remoteBySection.get(section);
    const hasLocal = hasLocalTripWorkspaceSection(uid, trip.id, section);

    if (!hasLocal) {
      if (remote) {
        applyTripWorkspaceSection(uid, trip.id, section, remote.data);
        writeWorkspaceBase(uid, trip.id, section, remote.data, remote.revision);
        localChanged = true;
      }
      continue;
    }

    if (role === "viewer" || (section === "participants" && role !== "owner")) {
      if (remote) {
        applyTripWorkspaceSection(uid, trip.id, section, remote.data);
        writeWorkspaceBase(uid, trip.id, section, remote.data, remote.revision);
        localChanged = true;
      }
      continue;
    }

    const local = exportTripWorkspaceSection(uid, trip.id, section);
    const persisted = readWorkspaceBase(uid, trip.id, section);
    const baseData = persisted?.data ?? null;
    const remoteData = remote?.data ?? null;
    const remoteRevision = remote?.revision ?? 0;
    const merged = remote
      ? mergeConcurrentData(baseData, local, remoteData)
      : local;

    if (stableSerialize(local) !== stableSerialize(merged)) {
      applyTripWorkspaceSection(uid, trip.id, section, merged);
      localChanged = true;
    }

    if (!remote || stableSerialize(remoteData) !== stableSerialize(merged)) {
      try {
        const saved = await saveSection({
          tripId: trip.id, section, data: merged, baseData: remoteData, baseRevision: remoteRevision
        });
        const nextRevision = Number.isFinite(Number(saved.data?.revision)) ? Number(saved.data.revision) : remoteRevision;
        writeWorkspaceBase(uid, trip.id, section, merged, nextRevision);
      } catch {
        // The active-trip synchronizer retries; never erase local data on failure.
      }
    } else {
      writeWorkspaceBase(uid, trip.id, section, remoteData, remoteRevision);
    }
  }
  return localChanged;
}

export function startUserTripListSync(input: {
  uid: string;
  onChange: (trips: TripSummary[]) => void;
}): () => void {
  let stopped = false;
  let running = false;
  const unsubscribers: Unsubscribe[] = [];
  const sharedTripUnsubscribers = new Map<string, Unsubscribe>();
  const expenseWorkspaceUnsubscribers = new Map<string, Unsubscribe>();
  const listRemote = httpsCallable<Record<string, never>, { trips?: TripSummary[] }>(functions, "listUserTripSummaries");

  const ensureExpenseRealtime = () => {
    if (stopped) return;
    const activeTrips = listTrips(input.uid);
    const activeIds = new Set(activeTrips.map((trip) => trip.id));
    for (const trip of activeTrips) {
      if (expenseWorkspaceUnsubscribers.has(trip.id)) continue;
      const unsubscribe = onSnapshot(doc(db, "trips", trip.id, "workspace", "expenses"), (snapshot) => {
        if (stopped || !snapshot.exists()) return;
        const cloud = snapshot.data() as CloudSectionDocument;
        const data = cloud.data ?? [];
        const local = exportTripWorkspaceSection(input.uid, trip.id, "expenses");
        const revision = Number.isFinite(Number(cloud.revision)) ? Number(cloud.revision) : 0;
        const persisted = readWorkspaceBase(input.uid, trip.id, "expenses");
        if (persisted && revision < persisted.revision) return;
        const currentTrip = listTrips(input.uid).find(item => item.id === trip.id);
        if (!currentTrip) return;
        const role = currentTrip.ownerUid === input.uid ? "owner" : normalizeTripRole(currentTrip.memberRole);
        const merged = role !== "viewer" && hasLocalTripWorkspaceSection(input.uid, trip.id, "expenses")
          ? mergeConcurrentData(persisted?.data ?? null, local, data)
          : data;
        // Store the actual cloud base, so existing reconciliation can commit
        // retained local edits rather than mistaking them for acknowledged data.
        writeWorkspaceBase(input.uid, trip.id, "expenses", data, revision);
        if (stableSerialize(local) === stableSerialize(merged)) return;
        applyTripWorkspaceSection(input.uid, trip.id, "expenses", merged);
        window.dispatchEvent(new CustomEvent("tripai:remote-workspace-updated", { detail: { uid: input.uid, tripId: trip.id, section: "expenses" } }));
      }, () => { void reconcileFromServer(); });
      expenseWorkspaceUnsubscribers.set(trip.id, unsubscribe);
    }
    for (const [tripId, unsubscribe] of expenseWorkspaceUnsubscribers) {
      if (activeIds.has(tripId)) continue;
      unsubscribe();
      expenseWorkspaceUnsubscribers.delete(tripId);
    }
  };

  const emit = () => {
    if (stopped) return;
    ensureExpenseRealtime();
    input.onChange(listTrips(input.uid));
  };

  async function reconcileFromServer() {
    if (stopped || running) return;
    running = true;
    try {
      await flushPendingTripSummaryCloudSync(input.uid);
      const result = await listRemote({});
      if (!Array.isArray(result.data?.trips)) throw new Error("INVALID_TRIP_SNAPSHOT");
      const remote = result.data.trips;
      reconcileOwnedTripSummariesFromServer(input.uid, remote.filter(trip => trip.ownerUid === input.uid), Object.keys(readPendingTripSummaries(input.uid)));
      for (const trip of remote) if (trip.ownerUid !== input.uid) upsertTripSummary(input.uid, trip);
      emit();

      // Migrate/synchronize every cached workspace in the background, not only the
      // currently open trip. This is what makes legacy phone data appear on the PC
      // without requiring the user to manually open every trip on every device.
      let workspaceChanged = false;
      for (const trip of listTrips(input.uid)) {
        if (stopped) break;
        try {
          if (await reconcileWorkspaceOnce(input.uid, trip)) workspaceChanged = true;
        } catch {
          // The periodic/focus reconciliation retries without damaging local data.
        }
      }
      if (workspaceChanged) emit();
    } catch {
      emit();
    } finally {
      running = false;
    }
  }

  // Owners receive true Firestore realtime updates, including trips created on another device.
  unsubscribers.push(onSnapshot(
    query(collection(db, "trips"), where("ownerUid", "==", input.uid)),
    (snapshot) => {
      const remote = snapshot.docs.map(entry => tripSummaryFromCloud(input.uid, entry.id, entry.data() as Record<string, any>));
      if (!snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites) {
        reconcileOwnedTripSummariesFromServer(input.uid, remote, Object.keys(readPendingTripSummaries(input.uid)));
      } else {
        for (const trip of remote) if(!listTrips(input.uid).some(local=>local.id===trip.id))upsertTripSummary(input.uid, trip);
      }
      emit();
    },
    () => { void reconcileFromServer(); }
  ));

  // Shared trips are discovered through the user's accepted invitations, then each trip
  // is followed in realtime. If rules on an older deployment reject the query, the
  // callable reconciliation below still keeps the list correct.
  unsubscribers.push(onSnapshot(
    query(collection(db, "tripInvitations"), where("targetUid", "==", input.uid)),
    (snapshot) => {
      const activeTripIds = new Set<string>();
      for (const entry of snapshot.docs) {
        const invitation = entry.data() as Record<string, any>;
        if (String(invitation.status || "") !== "accepted" || invitation.leftAt) continue;
        const tripId = String(invitation.tripId || "").trim();
        if (!tripId) continue;
        activeTripIds.add(tripId);
        if (sharedTripUnsubscribers.has(tripId)) continue;
        const unsubscribe = onSnapshot(doc(db, "trips", tripId), (tripSnapshot) => {
          if (!tripSnapshot.exists()) return;
          if(tripSnapshot.metadata.fromCache&&listTrips(input.uid).some(local=>local.id===tripId))return;
          upsertTripSummary(input.uid, tripSummaryFromCloud(input.uid, tripSnapshot.id, tripSnapshot.data() as Record<string, any>));
          emit();
        }, () => undefined);
        sharedTripUnsubscribers.set(tripId, unsubscribe);
      }
      for (const [tripId, unsubscribe] of sharedTripUnsubscribers) {
        if (activeTripIds.has(tripId)) continue;
        unsubscribe();
        sharedTripUnsubscribers.delete(tripId);
      }
      emit();
    },
    () => { void reconcileFromServer(); }
  ));

  const onVisible = () => {
    if (document.visibilityState === "visible") void reconcileFromServer();
  };
  const onFocus = () => void reconcileFromServer();
  const onOnline = () => void reconcileFromServer();
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onOnline);
  void reconcileFromServer();
  const safetyTimer = window.setInterval(() => void reconcileFromServer(), 30000);

  return () => {
    stopped = true;
    window.clearInterval(safetyTimer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("online", onOnline);
    for (const unsubscribe of unsubscribers) unsubscribe();
    for (const unsubscribe of sharedTripUnsubscribers.values()) unsubscribe();
    sharedTripUnsubscribers.clear();
    for (const unsubscribe of expenseWorkspaceUnsubscribers.values()) unsubscribe();
    expenseWorkspaceUnsubscribers.clear();
  };
}
